import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

const MODE_COLORS = {
  task_create:  { bg: 'rgba(54, 162, 235, 0.7)',  border: 'rgba(54, 162, 235, 1)' },
  task_update:  { bg: 'rgba(255, 159, 64, 0.7)',  border: 'rgba(255, 159, 64, 1)' },
  comment:      { bg: 'rgba(75, 192, 192, 0.7)',  border: 'rgba(75, 192, 192, 1)' },
}

const MODE_LABELS = {
  task_create: 'Feladat felvitel',
  task_update: 'Feladat módosítás',
  comment:     'Komment',
}

export default class extends Controller {
  static targets = ["countChart", "audioChart"]
  static values  = { chartsDataUrl: String }

  connect() {
    this.currentPeriod = 'weekly'
    this.countChartInstance = null
    this.audioChartInstance = null
    this.loadChartData()
  }

  switchPeriod(event) {
    const period = event.currentTarget.dataset.period
    if (period === this.currentPeriod) return

    this.currentPeriod = period
    event.currentTarget.parentElement.querySelectorAll('button').forEach(btn => {
      btn.classList.remove('active')
    })
    event.currentTarget.classList.add('active')

    this.loadChartData()
  }

  async loadChartData() {
    try {
      const url = new URL(this.chartsDataUrlValue, window.location.origin)
      url.searchParams.set('period', this.currentPeriod)
      const response = await fetch(url.toString(), { credentials: 'same-origin' })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this.renderCharts(data)
    } catch (error) {
      console.error('[VoiceCharts] Error loading chart data:', error)
      this.renderCharts({ labels: [], datasets: {}, period: this.currentPeriod })
    }
  }

  renderCharts(data) {
    const labels    = data.labels || []
    const datasets  = data.datasets || {}
    const formatted = this._formatLabels(labels)

    this._renderCountChart(formatted, datasets)
    this._renderAudioChart(formatted, datasets)
  }

  _renderCountChart(labels, datasets) {
    if (this.countChartInstance) {
      this.countChartInstance.destroy()
      this.countChartInstance = null
    }
    if (!this.hasCountChartTarget) return

    const chartDatasets = Object.entries(datasets).map(([mode, series]) => ({
      label: MODE_LABELS[mode] || mode,
      data:  series.count || [],
      backgroundColor: (MODE_COLORS[mode] || {}).bg     || 'rgba(128,128,128,0.6)',
      borderColor:     (MODE_COLORS[mode] || {}).border || 'rgba(128,128,128,1)',
      borderWidth: 2,
    }))

    this.countChartInstance = new Chart(this.countChartTarget.getContext('2d'), {
      type: 'bar',
      data: { labels, datasets: chartDatasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top' } },
        scales: {
          x: { stacked: true },
          y: { stacked: true, beginAtZero: true },
        },
      },
    })
  }

  _renderAudioChart(labels, datasets) {
    if (this.audioChartInstance) {
      this.audioChartInstance.destroy()
      this.audioChartInstance = null
    }
    if (!this.hasAudioChartTarget) return

    const chartDatasets = Object.entries(datasets).map(([mode, series]) => ({
      label: MODE_LABELS[mode] || mode,
      data:  series.audio_seconds || [],
      backgroundColor: (MODE_COLORS[mode] || {}).bg     || 'rgba(128,128,128,0.6)',
      borderColor:     (MODE_COLORS[mode] || {}).border || 'rgba(128,128,128,1)',
      borderWidth: 2,
      tension: 0.3,
    }))

    this.audioChartInstance = new Chart(this.audioChartTarget.getContext('2d'), {
      type: 'line',
      data: { labels, datasets: chartDatasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top' } },
        scales: {
          x: { title: { display: true, text: this.currentPeriod === 'monthly' ? 'Hónap' : 'Hét' } },
          y: { beginAtZero: true, title: { display: true, text: 'Másodpercek' } },
        },
      },
    })
  }

  _formatLabels(labels) {
    return labels.map(label => {
      if (this.currentPeriod === 'monthly') {
        const [year, month] = label.split('-')
        return new Date(year, month - 1).toLocaleDateString('hu-HU', { month: 'short', year: 'numeric' })
      }
      const date = new Date(label)
      return date.toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' })
    })
  }
}
